/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback } from "react";

import { useMountedState } from "./useMountedState";
import { errorForm } from "@origin-digital/reporting-client";
import { ANALYTICS_FORM_NAME } from "@/constants";

export const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const retry = <T,>(
  operation: (...args: any[]) => Promise<T>,
  delay: number,
  numberOfRetry: number
): Promise<T> => {
  return new Promise((resolve: any, reject: any) => {
    return operation()
      .then(resolve)
      .catch((reason: any) => {
        if (reason.message === "The user aborted a request.")
          return reject(reason);

        if (reason.fetchResponse?.status === 500 && numberOfRetry > 0) {
          return wait(delay)
            .then(retry.bind(null, operation, delay, numberOfRetry - 1))
            .then(resolve)
            .catch(reject);
        }

        return reject(reason);
      });
  });
};

const apiErrorHandling = (error: any): Promise<Error> => {
  // handle cases:
  // - network failure
  // - api returns 4xx/5xx
  if (
    error &&
    typeof error === "object" &&
    Object.prototype.hasOwnProperty.call(error, "code") &&
    Object.prototype.hasOwnProperty.call(error, "fetchResponse")
  ) {
    const fetchResponse = error.fetchResponse;

    if (fetchResponse.data && fetchResponse.data.errors?.length) {
      const errors = fetchResponse.data.errors;

      let errorMessage = "";
      errors.forEach((errorItem: any, index: any) => {
        errorForm({
          formName: ANALYTICS_FORM_NAME,
          errorCode: errorItem.code,
          errorMessage: errorItem.detail,
        });
        errorMessage += `${errorItem.detail}${index > 0 ? "\n" : ""}`;
      });

      return Promise.resolve(new Error(errorMessage));
    }
  }

  // fallback case
  return Promise.resolve(new Error(error.message));
};

export interface RetryOption {
  delay: number;
  numberOfRetry: number;
}

/**
 * A custom hook to support retry calling in case getting error; ensure the calling is safe to be proceeded.
 */
export const useApi = () => {
  const { isMounted } = useMountedState();

  const safeCall = useCallback(
    async <T,>(
      func: (...args: any[]) => Promise<T>,
      retryOption?: RetryOption
    ): Promise<T | Error> => {
      const option = retryOption ?? { delay: 0, numberOfRetry: 1 };

      const wrappedFunc = () =>
        new Promise<T>((resolve, reject) => {
          func()
            .then((value) => {
              /* istanbul ignore next */
              if (isMounted()) {
                resolve(value);
              }
            })
            .catch((error) => {
              /* istanbul ignore next */
              if (isMounted()) {
                reject(error);
              }
            });
        });

      try {
        return await retry(wrappedFunc, option.delay, option.numberOfRetry);
      } catch (error: any) {
        const apiError = await apiErrorHandling(error);
        return apiError;
      }
    },
    []
  );

  return {
    call: safeCall,
  };
};
